/**
 * Chat Widget Registry
 * 
 * Maps widgetID to renderer component for Help Chat bot message widgets.
 * This is a lightweight registry separate from the Home widget system.
 * 
 * @see README.md for full widget specification
 */

import { defineAsyncComponent } from 'vue';
import ChatWidgetLink from './ChatWidgetLink.vue';

/**
 * Chat Widget Registry
 * Maps widgetID strings to their Vue component renderers
 * 
 * @type {Object.<string, import('vue').Component>}
 */
export const chatWidgetRegistry = {
  "link": ChatWidgetLink,
  "visualisation": defineAsyncComponent(() => import('../visualisations/VisualisationWidget.vue'))
};

/**
 * Widgets rendered below the message bubble across the full chat width
 * instead of inside the bubble.
 */
const fullWidthWidgets = new Set(["visualisation"]);

/**
 * @param {string} widgetID
 * @returns {boolean}
 */
export function isFullWidthWidget(widgetID) {
  return fullWidthWidgets.has(widgetID);
}

/**
 * Get renderer component for a given widgetID
 * 
 * @param {string} widgetID - The widget identifier (e.g., "link")
 * @returns {import('vue').Component|null} - Vue component or null if not registered
 * 
 * @example
 * const renderer = getWidgetRenderer("link");
 * if (renderer) {
 *   // Render the widget component with widgetData as props
 * } else {
 *   // Ignore widget, show message only
 * }
 */
export function getWidgetRenderer(widgetID) {
  return chatWidgetRegistry[widgetID] || null;
}

/**
 * Check if a widgetID is registered
 * 
 * @param {string} widgetID - The widget identifier to check
 * @returns {boolean} - True if the widget has a registered renderer
 */
export function isWidgetRegistered(widgetID) {
  return widgetID in chatWidgetRegistry;
}

export default chatWidgetRegistry;
